import React, { createContext, useContext, useState, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useI18n } from "@/contexts/I18nContext";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

const AuthDialogContext = createContext({ open: () => {} });

export function AuthDialogProvider({ children }) {
  const [isOpen, setIsOpen] = useState(false);
  const [initialMode, setInitialMode] = useState("login");
  const open = useCallback((mode = "login") => { setInitialMode(mode); setIsOpen(true); }, []);
  return (
    <AuthDialogContext.Provider value={{ open }}>
      {children}
      <AuthDialog isOpen={isOpen} setIsOpen={setIsOpen} initialMode={initialMode} />
    </AuthDialogContext.Provider>
  );
}

export const useAuthDialog = () => useContext(AuthDialogContext);

function AuthDialog({ isOpen, setIsOpen, initialMode }) {
  const { user, login, register, error } = useAuth();
  const { t } = useI18n();
  const [mode, setMode] = useState(initialMode);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);

  React.useEffect(() => { queueMicrotask(() => setMode(initialMode)); }, [initialMode, isOpen]);
  React.useEffect(() => { if (user && user.id) queueMicrotask(() => setIsOpen(false)); }, [user, setIsOpen]);

  const submit = async (e) => {
    e.preventDefault();
    queueMicrotask(() => setBusy(true));
    const ok = mode === "login"
      ? await login(email, password)
      : await register(email, password, name);
    queueMicrotask(() => setBusy(false));
    if (ok) queueMicrotask(() => setIsOpen(false));
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent className="max-w-md rounded-3xl">
        <DialogHeader>
          <DialogTitle className="text-2xl">
            {mode === "login" ? t("login") : t("register")}
          </DialogTitle>
          <DialogDescription className="text-sm text-[color:var(--ink-2)]">
            {mode === "login"
              ? t("auth_welcome_back")
              : t("auth_create_account")}
          </DialogDescription>
        </DialogHeader>
        <Tabs value={mode} onValueChange={setMode}>
          <TabsList className="grid grid-cols-2 mb-2">
            <TabsTrigger value="login" data-testid="tab-login">{t("login")}</TabsTrigger>
            <TabsTrigger value="register" data-testid="tab-register">{t("register")}</TabsTrigger>
          </TabsList>
          <TabsContent value="login" />
          <TabsContent value="register" />
        </Tabs>
        <form onSubmit={submit} className="space-y-4">
          {mode === "register" && (
            <div className="space-y-1.5">
              <Label htmlFor="auth-name">{t("name")}</Label>
              <Input id="auth-name" data-testid="auth-name-input" required value={name} onChange={(e) => setName(e.target.value)} />
            </div>
          )}
          <div className="space-y-1.5">
            <Label htmlFor="auth-email">{t("email")}</Label>
            <Input id="auth-email" data-testid="auth-email-input" required type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="auth-password">{t("password")}</Label>
            <Input id="auth-password" data-testid="auth-password-input" required type="password" minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} />
          </div>
          {error && (
            <p data-testid="auth-error" className="text-sm text-[color:var(--emergency)]">{error}</p>
          )}
          <Button
            type="submit"
            data-testid="auth-submit-btn"
            disabled={busy}
            className="w-full pill bg-[color:var(--navy)] hover:bg-[color:var(--navy-2)] text-white"
          >
            {busy ? "…" : (mode === "login" ? t("login") : t("register"))}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
